import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Heart, X, Play, Pause, Volume2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import MatchCelebration from './MatchCelebration';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DiscoverProfiles = ({ user }) => {
  const navigate = useNavigate();
  const [profiles, setProfiles] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [isPlaying, setIsPlaying] = useState(false);
  const [swiping, setSwiping] = useState(false);
  const [celebration, setCelebration] = useState(null);
  const audioRef = useRef(null);

  const fetchProfiles = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/discover/profiles`, {
        withCredentials: true,
        params: { skip: 0 }
      });
      setProfiles(response.data.profiles);
      setLoading(false);
    } catch (error) {
      console.error('Fetch profiles error:', error);
      toast.error('Failed to load profiles');
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProfiles();
  }, [fetchProfiles]);

  const currentProfile = profiles[currentIndex];

  const toggleAudio = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
        setIsPlaying(false);
      } else {
        audioRef.current.play();
        setIsPlaying(true);
      }
    }
  };

  const handleSwipe = async (action) => {
    if (!currentProfile || swiping) return;

    setSwiping(true);
    try {
      const response = await axios.post(
        `${API}/match/swipe`,
        {
          target_user_id: currentProfile.user.id,
          action: action
        },
        { withCredentials: true }
      );

      if (response.data.is_mutual) {
        setCelebration({ name: currentProfile.user.name, matchId: response.data.match_id });
      }

      // Move to next profile
      setIsPlaying(false);
      if (currentIndex < profiles.length - 1) {
        setCurrentIndex(currentIndex + 1);
      } else {
        // Fetch more profiles
        fetchProfiles();
        setCurrentIndex(0);
      }
    } catch (error) {
      console.error('Swipe error:', error);
      toast.error('Failed to process swipe');
    } finally {
      setSwiping(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-pink-50 via-white to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-pink-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading profiles...</p>
        </div>
      </div>
    );
  }

  if (!currentProfile) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-pink-50 via-white to-purple-50">
        <header className="glass border-b border-gray-200">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <button
              onClick={() => navigate('/')}
              className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              <span>Back to Dashboard</span>
            </button>
          </div>
        </header>
        <div className="flex items-center justify-center min-h-[80vh]">
          <div className="text-center">
            <h3 className="text-2xl font-bold text-gray-900 mb-2">No more profiles</h3>
            <p className="text-gray-600 mb-4">Check back later for new people!</p>
            <button onClick={() => navigate('/')} className="btn-primary">
              Back to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 via-white to-purple-50">
      {celebration && (
        <MatchCelebration
          matchName={celebration.name}
          onSendMessage={() => navigate(`/chat/${celebration.matchId}`)}
          onKeepSwiping={() => setCelebration(null)}
        />
      )}
      <header className="glass border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <button
            onClick={() => navigate('/')}
            className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 transition-colors"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Back to Dashboard</span>
          </button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-6 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-2">Discover</h2>
          <p className="text-gray-600">Listen and decide - personality first!</p>
        </div>

        <div className="max-w-md mx-auto">
          <div className="card relative overflow-hidden" data-testid="profile-card">
            {/* Profile Header */}
            <div className="text-center mb-6">
              <div className="relative w-32 h-32 mx-auto mb-4">
                <div className="w-32 h-32 rounded-full bg-gradient-to-br from-pink-400 to-purple-500 flex items-center justify-center text-white text-5xl font-bold">
                  {currentProfile.user.name[0].toUpperCase()}
                </div>
                {user?.user?.subscription_status === 'premium' && currentProfile.user.profile_photo && (
                  <img
                    src={currentProfile.user.profile_photo}
                    alt={currentProfile.user.name}
                    className="absolute inset-0 w-32 h-32 rounded-full object-cover"
                    data-testid="discover-profile-photo"
                  />
                )}
              </div>
              <h3 className="text-2xl font-bold text-gray-900">{currentProfile.user.name}</h3>
              {user?.user?.subscription_status !== 'premium' && (
                <p className="text-sm text-gray-500 mt-2">🔒 Photo locked - upgrade to unlock</p>
              )}
            </div>

            {/* Audio Player */}
            <div className="bg-gradient-to-r from-pink-50 to-purple-50 rounded-2xl p-6 mb-6">
              <audio
                ref={audioRef}
                src={currentProfile.voice_bio.audio_data}
                onEnded={() => setIsPlaying(false)}
                className="hidden"
              />
              
              <div className="flex items-center justify-center space-x-4 mb-4">
                <button
                  onClick={toggleAudio}
                  className="w-16 h-16 rounded-full bg-pink-500 hover:bg-pink-600 text-white flex items-center justify-center transition-all"
                  data-testid="play-audio-btn"
                >
                  {isPlaying ? (
                    <Pause className="w-8 h-8" />
                  ) : (
                    <Play className="w-8 h-8 ml-1" />
                  )}
                </button>
              </div>

              <div className="flex items-center justify-center space-x-2 text-sm text-gray-600">
                <Volume2 className="w-4 h-4" />
                <span>{isPlaying ? 'Playing voice bio...' : 'Tap to play voice bio'}</span>
              </div>

              {currentProfile.voice_bio.transcription && (
                <div className="mt-4 p-3 bg-white rounded-lg">
                  <p className="text-sm text-gray-700 italic">
                    "{currentProfile.voice_bio.transcription}"
                  </p>
                </div>
              )}
            </div>

            {/* Swipe Buttons */}
            <div className="grid grid-cols-2 gap-4">
              <button
                onClick={() => handleSwipe('pass')}
                disabled={swiping}
                className="py-4 rounded-full border-2 border-gray-300 hover:border-red-400 hover:bg-red-50 transition-all flex items-center justify-center space-x-2 disabled:opacity-50"
                data-testid="pass-btn"
              >
                <X className="w-6 h-6 text-red-500" />
                <span className="font-semibold text-gray-700">Pass</span>
              </button>
              <button
                onClick={() => handleSwipe('like')}
                disabled={swiping}
                className="py-4 rounded-full bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600 text-white transition-all flex items-center justify-center space-x-2 disabled:opacity-50"
                data-testid="like-btn"
              >
                <Heart className="w-6 h-6" />
                <span className="font-semibold">Like</span>
              </button>
            </div>

            {/* Progress Indicator */}
            <div className="mt-4 text-center text-sm text-gray-500">
              Profile {currentIndex + 1} of {profiles.length}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default DiscoverProfiles;